import { AlertTriangle, Info, X, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SSEEscalationPayload } from '@/types/chat';
import { CRISIS_PARAMS } from './resources';

interface EscalationBannerProps {
  escalation: SSEEscalationPayload;
  /** The family's companion, named in the critical message (#2566). */
  companionName: string;
  onDismiss: () => void;
}

const LEVEL_CONFIG = {
  // 2026-11-01: the paused thread's banner is calm, not an alarm; its words
  // invite the family back (DECISIONS "The crisis pause stays…").
  critical: {
    bg: 'bg-stevens-bg-3 border-stevens-rule',
    text: 'text-stevens-ink-2',
    icon: Info,
    labelKey: 'banner.critical.label',
    messageKey: 'banner.critical.message',
  },
  high: {
    bg: 'bg-status-warn border-status-warn-border',
    text: 'text-status-warn-fg',
    icon: AlertTriangle,
    labelKey: 'banner.high.label',
    messageKey: 'banner.high.message',
  },
  medium: {
    bg: 'bg-stevens-bg-3 border-stevens-rule',
    text: 'text-stevens-ink-2',
    icon: Info,
    labelKey: 'banner.medium.label',
    messageKey: null,
  },
} as const;

// #2109 R3-B8: the labels are `crisis:*` keys; the numbers are CRISIS_PARAMS,
// the same object CrisisCallout and CrisisDisclosure read. Two rows since the
// 2026-10-14 ruling: 988, and 911 for immediate danger; no text line.
const RESOURCE_ROWS = [
  { id: 'lifeline', labelKey: 'banner.resources.lifeline', value: CRISIS_PARAMS.lifeline },
  { id: 'emergency', labelKey: 'banner.resources.emergency', value: CRISIS_PARAMS.emergency },
] as const;

export function EscalationBanner({ escalation, companionName, onDismiss }: EscalationBannerProps) {
  const { t } = useTranslation('crisis');
  const config = LEVEL_CONFIG[escalation.level] ?? LEVEL_CONFIG.medium;
  const Icon = config.icon;
  const isCritical = escalation.level === 'critical';

  return (
    <div
      className={cn('border-b', config.bg)}
      role={isCritical ? 'status' : 'alert'}
    >
      <div className="flex items-center gap-3 px-4 py-2.5">
        <Icon className={cn('h-4 w-4 flex-shrink-0', config.text)} />
        <div className={cn('flex-1 min-w-0', config.text)}>
          <span className="text-body font-medium">
            {/* No priority code: "P1" is a staff word, and alarm words push a
                person in crisis further (Victor, 2026-10-11). */}
            {t(config.labelKey)}
          </span>
          {config.messageKey && (
            <p className="text-caption mt-0.5 opacity-80">{t(config.messageKey, { ...CRISIS_PARAMS, companion: companionName })}</p>
          )}
        </div>
        {!isCritical && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={onDismiss}
            aria-label={t('banner.dismiss')}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {isCritical && (
        <div className="px-4 pb-2.5 flex flex-wrap items-center gap-2">
          <Phone className={cn('h-3.5 w-3.5', config.text)} />
          {RESOURCE_ROWS.map((resource) => (
            <span
              key={resource.id}
              className={cn('text-caption', config.text)}
            >
              <span className="font-medium">{t(resource.labelKey, CRISIS_PARAMS)}:</span>{' '}
              <span className="opacity-80">{resource.value}</span>
              {resource !== RESOURCE_ROWS[RESOURCE_ROWS.length - 1] && (
                <span className="ml-2 opacity-40">|</span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
